import React, { useEffect, useState } from "react";
import {
  createPersona,
  updatePersona,
  uploadAsset,
  PersonaPayload,
} from "../services/scenarioAdminService";
import PersonaAvatar from "./PersonaAvatar";

export interface PersonaDraft {
  id?: string; // present iff editing
  personaKey?: string;
  name: string;
  role: string;
  description: string;
  bio: string;
  voiceId: string;
  instructions: string;
  avatarObjectKey?: string;
  avatarPreviewUrl?: string;
  avatarSourceUrl?: string;
  avatarFile?: File;
  accessibleDocumentKeys?: string[];
  accessibleDocumentIds: string[];
}

interface PersonaFormModalProps {
  open: boolean;
  persona: PersonaDraft | null; // null means "create new"
  accessibleDocumentOptions: { id: string; name: string }[];
  scenarioId: string;
  getToken: () => string | undefined;
  onSave: (persona: PersonaDraft) => void;
  onClose: () => void;
  // Blob-URL lifecycle is owned by the parent so previews survive modal
  // unmount and get revoked when the parent unmounts.
  createBlobUrl: (blob: Blob) => string;
  revokeBlobUrl: (url: string | undefined) => void;
}

const EMPTY_PERSONA: PersonaDraft = {
  name: "",
  role: "",
  description: "",
  bio: "",
  voiceId: "",
  instructions: "",
  avatarObjectKey: undefined,
  accessibleDocumentIds: [],
};

/**
 * Create/edit a single persona.
 *
 * Two-step asset upload: server-shaped object keys require a personaId, so for
 * new personas we POST /personas first (no avatar), then upload the avatar,
 * then PATCH /personas/:id with the resulting objectKey. The caller sees a
 * single Save action; the orchestration lives here.
 */
const PersonaFormModal: React.FC<PersonaFormModalProps> = ({
  open,
  persona,
  accessibleDocumentOptions,
  scenarioId,
  getToken,
  onSave,
  onClose,
  createBlobUrl,
  revokeBlobUrl,
}) => {
  const [form, setForm] = useState<PersonaDraft>(EMPTY_PERSONA);
  const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(persona ?? EMPTY_PERSONA);
      setPendingAvatar(persona?.avatarFile ?? null);
      setError(null);
    }
  }, [open, persona]);

  if (!open) return null;

  const isEdit = !!form.id;

  const update = <K extends keyof PersonaDraft>(k: K, v: PersonaDraft[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleAccessible = (docId: string) => {
    setForm((f) => {
      const has = f.accessibleDocumentIds.includes(docId);
      return {
        ...f,
        accessibleDocumentIds: has
          ? f.accessibleDocumentIds.filter((id) => id !== docId)
          : [...f.accessibleDocumentIds, docId],
      };
    });
  };

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("Avatar exceeds 10 MB limit.");
      return;
    }
    setPendingAvatar(file);
    revokeBlobUrl(form.avatarPreviewUrl);
    update("avatarPreviewUrl", createBlobUrl(file));
    setError(null);
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.role.trim()) return "Role is required.";
    if (!form.instructions.trim()) return "Instructions are required.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const token = getToken();
      const payload: PersonaPayload = {
        name: form.name.trim(),
        role: form.role.trim(),
        description: form.description.trim() || undefined,
        bio: form.bio.trim() || undefined,
        voiceId: form.voiceId.trim() || null,
        instructions: form.instructions,
        accessibleDocumentIds: form.accessibleDocumentIds,
        avatarObjectKey: form.avatarObjectKey,
      };

      let personaId = form.id;
      if (!isEdit) {
        // Step 1: create the persona row so we have a server-known id to
        // shape the avatar object key against.
        const created = await createPersona(scenarioId, payload, token);
        personaId = created.id;
      } else {
        await updatePersona(scenarioId, personaId!, payload, token);
      }

      // Step 2: if a new avatar was staged, upload it.
      let finalAvatarKey = form.avatarObjectKey;
      if (pendingAvatar && personaId) {
        finalAvatarKey = await uploadAsset(
          "persona-avatar",
          { scenarioId, personaId },
          pendingAvatar,
          token,
        );
        // Step 3: patch the persona with the resulting object key.
        await updatePersona(
          scenarioId,
          personaId,
          { avatarObjectKey: finalAvatarKey },
          token,
        );
      }

      onSave({
        ...form,
        id: personaId,
        avatarObjectKey: finalAvatarKey,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save persona.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <h3 className="modal-title">
            {isEdit ? "Edit Persona" : "Add Persona"}
          </h3>
          <button
            onClick={onClose}
            disabled={busy}
            className="icon-action"
            aria-label="Close"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Name */}
          <div>
            <label className="form-label">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="form-control"
            />
          </div>

          {/* Role */}
          <div>
            <label className="form-label">
              Role
            </label>
            <input
              type="text"
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              className="form-control"
            />
          </div>

          {/* Description */}
          <div>
            <label className="form-label">Summary</label>
            <p className="text-xs text-slate-500 mb-1.5">
              One line, shown in the scenario briefing roster and above the
              profile in chat.
            </p>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={2}
              className="form-control"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="form-label">Profile</label>
            <p className="text-xs text-slate-500 mb-1.5">
              The full profile shown in the chat persona pane. Markdown, and
              player-facing — no secrets, motives or solutions. Those belong in
              Instructions below.
            </p>
            <textarea
              value={form.bio}
              onChange={(e) => update("bio", e.target.value)}
              rows={8}
              className="form-control"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="form-label">
              Instructions (LLM system prompt)
            </label>
            <textarea
              value={form.instructions}
              onChange={(e) => update("instructions", e.target.value)}
              rows={6}
              className="form-control form-control--mono"
            />
          </div>

          {/* Avatar */}
          <div>
            <label className="form-label">
              Avatar
            </label>
            <div className="flex items-center gap-4">
              <PersonaAvatar
                name={form.name}
                src={form.avatarPreviewUrl}
                className="w-16 h-16 rounded-full border border-outline-variant/30"
                textClassName="text-xl"
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarSelect}
                className="form-file-input"
              />
            </div>
            <p className="form-help">Max 10 MB.</p>
          </div>

          {/* Voice */}
          <div>
            <label className="form-label">Voice</label>
            <p className="text-xs text-slate-500 mb-1.5">
              Optional ElevenLabs voice ID, copied from the ElevenLabs
              dashboard. Leave blank to use the default voice.
            </p>
            <input
              type="text"
              value={form.voiceId}
              onChange={(e) => update("voiceId", e.target.value)}
              placeholder="Default voice"
              className="form-control form-control--mono"
            />
          </div>

          {/* Accessible documents */}
          <div>
            <label className="form-label form-label--spaced">
              Accessible Documents
            </label>
            {accessibleDocumentOptions.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                Add scenario documents first; you'll be able to grant this
                persona access to them here.
              </p>
            ) : (
              <ul className="space-y-1">
                {accessibleDocumentOptions.map((d) => (
                  <li key={d.id}>
                    <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.accessibleDocumentIds.includes(d.id)}
                        onChange={() => toggleAccessible(d.id)}
                      />
                      {d.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="button-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="button-primary"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonaFormModal;
